<template>
  <div
    ref="clickOutsideWrapper"
    class="filter-wrapper"
    @click="!disabledSelect && toggleOptions()"
  >
    <div
      @mouseenter="onFilterHover($event)"
      @mouseleave="onFilterUnhover()"
      class="filter-box"
      :class="{ 'select-required': required, 'filter-box--active': selectedOption }"
    >  
      <img v-if="loader" class="loader-gif" src="@/assets/loading_mini_green_no_background.gif" alt="" style="top: 4px; left: 2px; width: 35px;"  />
      <template v-else>
        <template v-if="!disabledSelect">
          <img
            v-if="selectedOption && allowEmpty && hiddenCrossVar"
            @click.stop="onCrossClick()"
            class="cross"
            :class="[
              `cross--${size}`,
              { 'cross--mobile': isMobile }
            ]"
            src="@/assets/restyle/images/shared/icons/cross_2.svg"
            alt=""
          />
          <img
            v-else
            class="search-icon-default"
            :class="[
              `search-icon-default--${size}`,
              { 'search-icon-default--mobile': isMobile }
            ]"
            src="@/assets/restyle/images/shared/icons/filter_search_default.svg"
            alt=""
          />
        </template>
      </template>
      <input
        type="text"
        ref="inputField"
        @input="inputChangeHandler($event)"
        @focus="onInputFocus()"
        @blur="onInputBlur()"
        @mouseenter="$emit('mouseenter', $event)"
        @mouseleave="$emit('mouseleave')"
        :value="inputText"
        :placeholder="placeholder"
        class="input-box"
        :class="[
          {
            'placeholder-active': !isFocused && selectedOption,
            'input-box--mobile': isMobile,
          },
          `input--${size}`,
        ]"
        :id="customId"
        :disabled="disabledSelect"
      />
      <div class="select-error" v-if="required">
        <img class="select-error-image" src="@/assets/restyle/images/shared/select/error.svg" alt=""/>
      </div>
      <img
        class="arrow-icon"
        :class="{ 'arrow-up': optionsVisible }"
        :src="require(`@/assets/restyle/images/arrow/arrow_sharp_down_${disabledSelect? 'white': 'gray'}.svg`)"
        alt="" />
    </div>
    <div
      v-if="optionsVisible"
      class="select-option-wrapper scrollbar scrollbar--secondary"
      :style="{ 'max-height': optionsMaxHeight }"
      :class="[`select-option-wrapper--${size}`]"
    >
      <div v-for="(option, index) in filteredOptions">
        <div @click="selectOption(option)" class="select-option" :key="`option-${index}`">
          <slot name="option" :option="option">
            {{ option.name }}
          </slot>
        </div>
        <template v-if="option?.children && useChildOptions">
          <div
            v-for="(subOption, subOptionIndex) in option.children"
            :key="`option-${index}-subOption-${subOptionIndex}`"
            @click="selectOption(subOption)"
            class="select-option child-option"
          >
            <slot name="option" :option="subOption">
              {{ subOption.name }}
            </slot>
          </div>
        </template>
      </div>
    </div>
    <div v-if="$slots.required && required" class="select-required-label">
      <slot name="required"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchFilterSelect',
  props: {
    inputText: {
      default: '',
      type: String,
      required: true,
    },
    placeholder: {
      default: null,
      type: String,
    },
    options: {
      required: true,
      type: Array
    },
    selectedOption: {
      default: null,
    },
    optionsMaxHeight: {
      default: '250px',
      type: String
    },
    showTooltip: {
      default: false,
      type: Boolean,
    },
    tooltipDelay: {
      default: 0,
      type: Number,
    },
    tooltipTail: {
      default: true,
      type: Boolean,
    },
    allowEmpty: {
      default: true,
      type: Boolean,
    },
    useChildOptions: {
      default: false,
      type: Boolean,
    },
    required: {
      default: false,
      type: Boolean
    },
    viewKey: {
      type: String,
      default: 'name',
    },
    customId: {
      type: String,
    },
    size: {
      type: String,
      default: 'normal',
    },
    disabledSelect: {
      type: Boolean,
      default: false,
    },
    hiddenCrossVar: {
      type: Boolean,
      default: true,
    },
    isNotCleanValue: {
      type: Boolean,
      default: false
    },
    loader: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFocused: false,
      optionsVisible: false,
      textOverflowing: false,
    }
  },
  watch: {
    optionsVisible(newValue) {
      if (!newValue && this.isNotCleanValue === false) {
        this.$emit('inputChange', '');
      }
    }
  },
  computed: {
    filteredOptions() {
      if (this.inputText && !this.useChildOptions) {
        let lowerCasedFilter = this.inputText.toLowerCase();
        let result = [];
        this.options.forEach(option => {
          if (option[this.viewKey].toLowerCase().includes(lowerCasedFilter)) {
            result.push(option);
          }
        });
        return result;
      }
      if (this.inputText && this.useChildOptions) {
        let lowerCasedFilter = this.inputText.toLowerCase();
        let result = [];
        this.options.forEach(option => {
          let matchedSubOptions = option.children?.filter(subOption =>
            subOption.name.toLowerCase().includes(lowerCasedFilter));
          if (option.name.toLowerCase().includes(lowerCasedFilter) || matchedSubOptions?.length > 0) {
            result.push({
              ...option,
              children: matchedSubOptions
            });
          }
        });
        return result;
      }
      return this.options;
    },
  },
  mounted() {
    document.addEventListener('click', this.clickOutsideClose);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.clickOutsideClose);
  },
  methods: {
    onFilterHover(event) {
      this.checkIfTextOverflowing();
      if (this.textOverflowing && this.showTooltip && event) {
        this.$store.dispatch('tooltip_store/SET_VIEW_TOOLTIP',
          { hoverEvent: event, tooltipText: this.placeholder, tooltipDelay: this.tooltipDelay, tooltipTail: this.tooltipTail });
      }
    },
    onFilterUnhover() {
      if (this.showTooltip) {
        this.$store.dispatch('tooltip_store/CLEAR_VIEW_TOOLTIP');
      }
    },
    toggleOptions() {
      this.optionsVisible = !this.optionsVisible;
    },
    clickOutsideClose(event) {
      if (!this.$refs.clickOutsideWrapper?.contains(event.target)) {
        this.optionsVisible = false;
      }
    },
    selectOption(option) {
      this.optionsVisible = false;
      this.$emit('selectChange', option);
    },
    inputChangeHandler(event) {
      this.$emit('inputChange', event.target.value);
    },
    onCrossClick() {
      this.$emit('inputChange', '');
      this.optionsVisible = false;
      this.$emit('selectChange', null);
    },
    checkIfTextOverflowing() {
      const inputElement = this.$refs.inputField;
      const inputValueBefore = inputElement.value;
      inputElement.value = inputElement.placeholder;
      this.textOverflowing = inputElement.scrollWidth > inputElement.clientWidth;
      inputElement.value = inputValueBefore;
    },
    onInputFocus() {
      if (this.showTooltip) {
        this.$store.dispatch('tooltip_store/CLEAR_VIEW_TOOLTIP');
      }
      this.isFocused = true;
    },
    onInputBlur() {
      this.isFocused = false;
    }

  }
}
</script>

<style lang="scss" scoped>
.input-box {
  padding: 9px 40px 9px 40px;
  border-radius: 10px;
  background-color: #E1F5F5;
  font-weight: 400;
  font-size: 18px;
  line-height: 25px;
  color: $color-page-text;
  
  &--mobile {
    padding-left: 35px;
  }
}

.search-icon-default {
  width: 18px;
  height: 18px;
  position: absolute;
  top: calc(33% - 1px);
  left: 12px;

  &--small {
    width: 12px;
    height: 12px;
  }

  &--mobile {
    width: 12px;
    height: 12px;
    top: calc(33% + 2px);
  }
}
input {
  &:focus {
    outline: 1px solid $color-smartro-green-bright;
    box-shadow: none;
  }
  &:disabled {
    padding-left: 12px;
    background: #EEF1F4;
    outline: 1px solid #D1D9E6;
  }
}
input::placeholder {
  color: $color-gray-text;
  font-family: Noto Sans;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  color: $color-page-text;
  font-family: Noto Sans;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
input {
  width: 100%;
}
input.placeholder-active::placeholder {
  color: $color-page-text;
}
.select-option {
  overflow-wrap: break-word;

  &:hover {
    background: #1D1871;
    color: #FFFFFF;
    cursor: pointer;
    & *{
      color: #FFFFFF;
    }
  }

  padding: 10px 20px 10px 20px;

  &:first-child {
    border-radius: 9.4px 9.4px 0 0;
  }

  &:last-child {
    border-radius: 0 0 9.4px 9.4px;
  }
}
.select-option-wrapper {
  border-radius: 9.4px;
  box-shadow: 0px 3.8px 15.1px rgba(23, 21, 55, 0.1);
  background: #FFFFFF;
  position: absolute;
  z-index: 1;
  font-size: 20px;
  line-height: 27px;
  width: 100%;
  overflow-y: auto;
  max-height: 250px;
  top: 49px;

  &--small {
    top: 34px;
  }
}
.arrow-icon {
  position: absolute;
  top: calc(33% + 4px);
  right: 12px;
  width: 11px;
  height: 11px;
}
.arrow-up {
  transform: rotate(180deg);
}
.filter-box {
  width: 100%;

  &--active {
    border-radius: 10px;
    outline: 1px solid #466ABF;
    & input {
      box-shadow: none;
    }
  }
  &:hover {
    & input {
      outline: 1px solid $color-smartro-green-bright;
      box-shadow: none;
      &:disabled {
        outline: 1px solid #D1D9E6;
      }
    }
  }
}
.cross {
  width: 15px;
  height: 15px;
  cursor: pointer;
  position: absolute;
  top: 33%;
  left: 12px;

  &--small {
    left: 10px;
    width: 12px;
    height: 12px;
  }

  &--mobile {
    width: 12px;
    height: 12px;
    top: calc(33% + 2px);
  }
}
.child-option {
  padding: 10px 20px 10px 50px;
}
.select-required {
  & input {
    outline: 1px solid #EB6363 !important;
    padding-right: 65px;
  }
  &-label {
    position: absolute;
    font-weight: 400;
    font-size: 18px;
    line-height: 25px;
    color: #EB6363;
    bottom: -25px;
  }
}
.select-error {
  position: absolute;
  right: 30px;
  top: calc(33% - 3px);
  width: 21px;
  height: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  &-image {
    width: 21px;
    height: 21px;
  }
}

.input {
  &--small {
    padding: 4px 27px 4px 27px;
    font-size: 16px;
  }
}

.loader-gif {
  position: absolute;
  top: 0;
  left: 0;
}
</style>